
/* ============================== src/components/Reports.jsx ============================== */
// path: frontend/src/components/Reports.jsx
/**
 * Отчёты. useReportsDb отдаёт scope-фильтрованные задачи/проекты/сотрудников.
 * capabilities.seeWipReport — вкладка «Выручка по НЗП»; сервер валидирует
 * через ReportService (requireAuth + canSeeWipReport на бэке).
 *
 * savedFiltersKey = `savedReportFilters:${ur.id}` — ключ localStorage
 * привязан к пользователю. Раньше был `:me` (все делили один), из-за
 * чего сохранённые фильтры разных пользователей перекрывались.
 */
import { useEffect, useMemo, useState } from "react";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../hooks/useAuth";
import { useDataHelpers } from "../hooks/useDataHelpers";
import { useFilters } from "../hooks/useFilters";
import { useWipReportQuery } from "../api/queries/reports";
import { Select } from "./Select";
import { Ic } from "./Icons";
import { ICONS } from "./iconsData";
import {
  TASK_STATUSES,
  PRIORITIES,
  PROJECT_STATUSES,
} from "../utils/constants";
import { optionsFromList, optionsFromMap } from "../utils/selectOptions";
import { fmtDMY } from "../utils/date";
import { getPrimaryDeptName } from "../utils/helpers";
import { downloadCsv } from "../utils/csvExport";
import {
  formatRub,
  formatRate,
  formatRubCsv,
  formatRateCsv,
} from "../utils/format";
import { ColoredSpan } from "./ColoredSpan";

const REPORT_TYPES = [
  { value: "tasks", label: "Задачи" },
  { value: "projects", label: "Проекты" },
  { value: "employees", label: "Сотрудники" },
  { value: "worklog", label: "Трудозатраты" },
  { value: "wip", label: "Выручка по НЗП" },
];

const INITIAL_FILTERS = Object.freeze({
  type: "tasks",
  dateFrom: "",
  dateTo: "",
  deadlineFrom: "",
  deadlineTo: "",
  closedFrom: "",
  closedTo: "",
  projectId: "all",
  assigneeId: "all",
  status: "all",
  priority: "all",
  customer: "",
});

const EMPTY_STATS = { plan: 0, fact: 0, count: 0 };

const STATUS_SELECT_OPTIONS = optionsFromMap(TASK_STATUSES, "Все");
const PRIORITY_SELECT_OPTIONS = optionsFromMap(PRIORITIES, "Все");

function Reports({ db, ur }) {
  const { capabilities } = useAuth();
  const { showToast } = useToast();
  const { empName, getTaskSpent } = useDataHelpers(db);

  const savedFiltersKey = `savedReportFilters:${ur?.id ?? "anon"}`;

  const safeDb = useMemo(() => {
    if (!db) {
      return {
        projects: [],
        tasks: [],
        employees: [],
        departments: [],
        kbs: [],
        vacations: [],
      };
    }
    return db;
  }, [db]);

  const { filters, setFilter, resetFilters } = useFilters(INITIAL_FILTERS);
  const {
    type: filterType,
    dateFrom,
    dateTo,
    deadlineFrom,
    deadlineTo,
    closedFrom,
    closedTo,
    projectId,
    assigneeId,
    status,
    priority,
    customer,
  } = filters;

  const [savedFilters, setSavedFilters] = useState(() => {
    try {
      const data = localStorage.getItem(savedFiltersKey);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  });
  const [filterName, setFilterName] = useState("");

  const allProjects = useMemo(() => safeDb.projects || [], [safeDb.projects]);
  const allEmployees = useMemo(
    () => safeDb.employees || [],
    [safeDb.employees],
  );

  const projectsById = useMemo(
    () => new Map(allProjects.map((p) => [p.id, p])),
    [allProjects],
  );
  const employeesById = useMemo(
    () => new Map(allEmployees.map((e) => [e.id, e])),
    [allEmployees],
  );

  const canSeeWip = Boolean(capabilities.seeWipReport);

  const { data: wipReportData = [] } = useWipReportQuery(
    projectId === "all" ? null : projectId,
    dateFrom || null,
    dateTo || null,
    { enabled: filterType === "wip" && canSeeWip },
  );

  const projectStatsById = useMemo(() => {
    const map = new Map();
    (safeDb.tasks || []).forEach((t) => {
      if (t.archived) return;
      let s = map.get(t.projectId);
      if (!s) {
        s = { plan: 0, fact: 0, count: 0 };
        map.set(t.projectId, s);
      }
      s.plan += t.plannedHours || 0;
      if (Array.isArray(t.logs)) for (const l of t.logs) s.fact += l.hours || 0;
      s.count += 1;
    });
    return map;
  }, [safeDb.tasks]);

  const employeeStatsById = useMemo(() => {
    const map = new Map();
    (safeDb.tasks || []).forEach((t) => {
      if (!t.assigneeId) return;
      let s = map.get(t.assigneeId);
      if (!s) {
        s = { plan: 0, fact: 0, count: 0 };
        map.set(t.assigneeId, s);
      }
      s.plan += t.plannedHours || 0;
      if (Array.isArray(t.logs)) for (const l of t.logs) s.fact += l.hours || 0;
      s.count += 1;
    });
    return map;
  }, [safeDb.tasks]);

  const projectSelectOptions = useMemo(
    () =>
      optionsFromList(allProjects, "Все проекты", (p) => ({
        value: p.id,
        label: `${p.code} - ${p.name}`,
      })),
    [allProjects],
  );

  const assigneeSelectOptions = useMemo(
    () =>
      optionsFromList(allEmployees, "Все", (e) => ({
        value: e.id,
        label: `${e.last} ${e.first}`,
      })),
    [allEmployees],
  );

  const availableReportTypes = useMemo(
    () => REPORT_TYPES.filter((t) => t.value !== "wip" || canSeeWip),
    [canSeeWip],
  );

  useEffect(() => {
    if (filterType === "wip" && !canSeeWip) {
      setFilter("type", "tasks");
    }
  }, [filterType, canSeeWip, setFilter]);

  const handleFilterChange = setFilter;

  const results = useMemo(() => {
    const tasksList = safeDb.tasks || [];
    const projectsList = safeDb.projects || [];
    const employeesList = safeDb.employees || [];

    if (filterType === "tasks") {
      let tasks = tasksList;
      if (dateFrom || dateTo) {
        tasks = tasks.filter((t) => {
          if (!t.createdAt) return false;
          if (dateFrom && t.createdAt < dateFrom) return false;
          if (dateTo && t.createdAt > dateTo) return false;
          return true;
        });
      }
      if (deadlineFrom || deadlineTo) {
        tasks = tasks.filter((t) => {
          if (!t.deadline) return false;
          if (deadlineFrom && t.deadline < deadlineFrom) return false;
          if (deadlineTo && t.deadline > deadlineTo) return false;
          return true;
        });
      }
      if (closedFrom || closedTo) {
        tasks = tasks.filter((t) => {
          if (!t.closedAt) return false;
          if (closedFrom && t.closedAt < closedFrom) return false;
          if (closedTo && t.closedAt > closedTo) return false;
          return true;
        });
      }
      if (projectId !== "all")
        tasks = tasks.filter((t) => t.projectId === projectId);
      if (assigneeId !== "all")
        tasks = tasks.filter((t) => t.assigneeId === assigneeId);
      if (status !== "all") tasks = tasks.filter((t) => t.status === status);
      if (priority !== "all")
        tasks = tasks.filter((t) => t.priority === priority);
      if (customer) {
        const q = customer.toLowerCase();
        tasks = tasks.filter((t) => {
          const project = projectsById.get(t.projectId);
          return project && project.customer?.toLowerCase().includes(q);
        });
      }
      return tasks;
    }

    if (filterType === "projects") {
      let projects = projectsList;
      if (dateFrom) projects = projects.filter((p) => p.start >= dateFrom);
      if (dateTo) projects = projects.filter((p) => p.start <= dateTo);
      if (deadlineFrom)
        projects = projects.filter((p) => p.end && p.end >= deadlineFrom);
      if (deadlineTo)
        projects = projects.filter((p) => p.end && p.end <= deadlineTo);
      if (closedFrom)
        projects = projects.filter(
          (p) => p.closedAt && p.closedAt >= closedFrom,
        );
      if (closedTo)
        projects = projects.filter((p) => p.closedAt && p.closedAt <= closedTo);
      if (projectId !== "all")
        projects = projects.filter((p) => p.id === projectId);
      if (customer) {
        const q = customer.toLowerCase();
        projects = projects.filter((p) =>
          p.customer?.toLowerCase().includes(q),
        );
      }
      return projects;
    }

    if (filterType === "employees") {
      let employees = employeesList;
      if (projectId !== "all") {
        const taskIds = tasksList
          .filter((t) => t.projectId === projectId)
          .map((t) => t.id);
        employees = employees.filter((e) =>
          tasksList.some(
            (t) => t.assigneeId === e.id && taskIds.includes(t.id),
          ),
        );
      }
      if (assigneeId !== "all")
        employees = employees.filter((e) => e.id === assigneeId);
      if (customer) {
        const q = customer.toLowerCase();
        employees = employees.filter((e) => {
          const userTasks = tasksList.filter((t) => t.assigneeId === e.id);
          if (userTasks.length === 0) return false;
          return userTasks.some((t) => {
            const project = projectsById.get(t.projectId);
            return project && project.customer?.toLowerCase().includes(q);
          });
        });
      }
      return employees;
    }

    if (filterType === "worklog") {
      let logs = [];
      tasksList.forEach((t) => {
        (t.logs || []).forEach((l) => {
          logs.push({
            ...l,
            taskTitle: t.title,
            projectId: t.projectId,
            assigneeId: t.assigneeId,
          });
        });
      });
      if (dateFrom) logs = logs.filter((l) => l.date >= dateFrom);
      if (dateTo) logs = logs.filter((l) => l.date <= dateTo);
      if (projectId !== "all")
        logs = logs.filter((l) => l.projectId === projectId);
      if (assigneeId !== "all")
        logs = logs.filter((l) => l.userId === assigneeId);
      if (customer) {
        const q = customer.toLowerCase();
        logs = logs.filter((l) => {
          const project = projectsById.get(l.projectId);
          return project && project.customer?.toLowerCase().includes(q);
        });
      }
      return logs;
    }

    if (filterType === "wip") {
      return wipReportData.map((r) => {
        const p = projectsById.get(r.projectId) || r.project || {};
        return {
          project: {
            id: r.projectId,
            code: p.code || r.projectCode || "",
            contractNumber: p.contractNumber || r.contractNumber || "",
            customer: p.customer || r.customer || "",
          },
          hours: r.hours || 0,
          rate: r.rate || null,
          revenue: r.revenue || null,
        };
      });
    }

    return [];
  }, [
    filterType,
    dateFrom,
    dateTo,
    deadlineFrom,
    deadlineTo,
    closedFrom,
    closedTo,
    projectId,
    assigneeId,
    status,
    priority,
    customer,
    safeDb,
    projectsById,
    wipReportData,
  ]);

  const saveFilter = () => {
    if (!filterName.trim()) {
      showToast("Введите название фильтра", "warning");
      return;
    }
    const newFilter = {
      id: Date.now(),
      name: filterName.trim(),
      filters: { ...filters },
    };
    const updated = [...savedFilters, newFilter];
    setSavedFilters(updated);
    localStorage.setItem(savedFiltersKey, JSON.stringify(updated));
    setFilterName("");
    showToast("Фильтр сохранён", "success");
  };

  const loadFilter = (filter) => {
    Object.entries(filter.filters).forEach(([key, value]) =>
      setFilter(key, value),
    );
    setFilterName(filter.name);
  };

  const deleteSavedFilter = (id) => {
    const updated = savedFilters.filter((f) => f.id !== id);
    setSavedFilters(updated);
    localStorage.setItem(savedFiltersKey, JSON.stringify(updated));
  };

  const buildReportRows = () => {
    const header =
      {
        tasks: [
          "#",
          "Задача",
          "Проект",
          "Исполнитель",
          "Статус",
          "Приоритет",
          "План (ч)",
          "Факт (ч)",
          "Срок исполнения",
          "Закрыто",
        ],
        projects: [
          "#",
          "Код",
          "Проект",
          "Статус",
          "Заказчик",
          "План (ч)",
          "Факт (ч)",
          "Ответственный",
          "Закрыто",
        ],
        employees: [
          "#",
          "Сотрудник",
          "Отдел (основной)",
          "План (ч)",
          "Факт (ч)",
          "Кол-во задач",
        ],
        worklog: [
          "#",
          "Дата",
          "Сотрудник",
          "Задача",
          "Проект",
          "Часы",
          "Комментарий",
        ],
        wip: [
          "#",
          "Проект (код)",
          "Номер договора",
          "Заказчик",
          "Ставка ₽/ч",
          "Часы НЗП за период",
          "Выручка ₽",
        ],
      }[filterType] || [];

    const rows = [header];

    if (filterType === "tasks") {
      results.forEach((t, idx) => {
        const project = projectsById.get(t.projectId);
        rows.push([
          idx + 1,
          t.title || "",
          project?.code || "",
          t.assigneeId ? empName(t.assigneeId) : "",
          TASK_STATUSES[t.status]?.label || t.status || "",
          PRIORITIES[t.priority]?.label || t.priority || "",
          t.plannedHours ?? "",
          getTaskSpent(t),
          t.deadline ? fmtDMY(t.deadline) : "",
          t.closedAt ? fmtDMY(t.closedAt) : "",
        ]);
      });
    } else if (filterType === "projects") {
      results.forEach((p, idx) => {
        const stats = projectStatsById.get(p.id) || EMPTY_STATS;
        rows.push([
          idx + 1,
          p.code || "",
          p.name || "",
          PROJECT_STATUSES[p.status] || p.status || "",
          p.customer || "",
          p.plannedHours ?? "",
          stats.fact,
          p.managerId ? empName(p.managerId) : "",
          p.closedAt ? fmtDMY(p.closedAt) : "",
        ]);
      });
    } else if (filterType === "employees") {
      results.forEach((e, idx) => {
        const stats = employeeStatsById.get(e.id) || EMPTY_STATS;
        rows.push([
          idx + 1,
          `${e.last || ""} ${e.first || ""}`.trim(),
          getPrimaryDeptName(e, safeDb),
          stats.plan,
          stats.fact,
          stats.count,
        ]);
      });
    } else if (filterType === "worklog") {
      results.forEach((l, idx) => {
        const project = projectsById.get(l.projectId);
        const user = employeesById.get(l.userId);
        rows.push([
          idx + 1,
          fmtDMY(l.date),
          user ? `${user.last} ${user.first}` : "",
          l.taskTitle || "",
          project?.code || "",
          l.hours ?? "",
          l.note || "",
        ]);
      });
    } else if (filterType === "wip") {
      results.forEach((r, idx) => {
        rows.push([
          idx + 1,
          r.project.code || "",
          r.project.contractNumber || "",
          r.project.customer || "",
          formatRateCsv(r.rate),
          r.hours,
          formatRubCsv(r.revenue),
        ]);
      });
    }

    return rows;
  };

  const buildFileName = () => {
    const from = dateFrom || closedFrom || "start";
    const to = dateTo || closedTo || "end";
    if (filterType === "wip") return `report_wip_${from}_${to}`;
    return `report_${filterType}_${from}_${to}`;
  };

  const handleExport = () => {
    if (results.length === 0) {
      showToast("Нет данных для выгрузки", "warning");
      return;
    }
    downloadCsv(buildFileName(), buildReportRows());
    showToast(`Выгружено строк: ${results.length}`, "success");
  };

  const renderResults = () => {
    if (results.length === 0) {
      return (
        <div className="empty-note p-4">
          Нет данных, соответствующих фильтрам
        </div>
      );
    }

    if (filterType === "tasks") {
      return (
        <div className="w-full overflow-x-auto">
          <table className="tbl tbl-report">
            <thead>
              <tr>
                <th>#</th>
                <th>Задача</th>
                <th>Проект</th>
                <th>Исполнитель</th>
                <th>Статус</th>
                <th>Приоритет</th>
                <th>План (ч)</th>
                <th>Факт (ч)</th>
                <th>Срок исполнения</th>
                <th>Закрыто</th>
              </tr>
            </thead>
            <tbody>
              {results.map((t, idx) => {
                const project = projectsById.get(t.projectId);
                const statusDef = TASK_STATUSES[t.status] || {
                  label: t.status || "Неизвестно",
                  color: "#64748b",
                };
                const priorityDef = PRIORITIES[t.priority] || {
                  label: t.priority || "Неизвестно",
                  color: "#64748b",
                };
                return (
                  <tr key={t.id}>
                    <td>{idx + 1}</td>
                    <td>
                      <b>{t.title}</b>
                    </td>
                    <td>{project?.code || "-"}</td>
                    <td>{t.assigneeId ? empName(t.assigneeId) : "-"}</td>
                    <td>
                      <ColoredSpan
                        color={statusDef.color}
                        className="st-chip st-chip--tinted"
                      >
                        {statusDef.label}
                      </ColoredSpan>
                    </td>
                    <td>
                      <ColoredSpan
                        color={priorityDef.color}
                        className="rep-priority"
                      >
                        {priorityDef.label}
                      </ColoredSpan>
                    </td>
                    <td>{t.plannedHours ?? "-"}</td>
                    <td>{getTaskSpent(t)}</td>
                    <td>{t.deadline ? fmtDMY(t.deadline) : "-"}</td>
                    <td>{fmtDMY(t.closedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    if (filterType === "projects") {
      return (
        <div className="w-full overflow-x-auto">
          <table className="tbl tbl-report tbl-report--projects">
            <thead>
              <tr>
                <th>#</th>
                <th>Код</th>
                <th>Проект</th>
                <th>Статус</th>
                <th>Заказчик</th>
                <th>План (ч)</th>
                <th>Факт (ч)</th>
                <th>Ответственный</th>
                <th>Закрыто</th>
              </tr>
            </thead>
            <tbody>
              {results.map((p, idx) => {
                const stats = projectStatsById.get(p.id) || EMPTY_STATS;
                return (
                  <tr key={p.id}>
                    <td>{idx + 1}</td>
                    <td>
                      <b>{p.code}</b>
                    </td>
                    <td>{p.name}</td>
                    <td>
                      <span
                        className={`st-chip ${p.status === "active" ? "active" : ""}`}
                      >
                        {PROJECT_STATUSES[p.status] || p.status}
                      </span>
                    </td>
                    <td>{p.customer || "-"}</td>
                    <td>{p.plannedHours ?? "-"}</td>
                    <td>{stats.fact}</td>
                    <td>{empName(p.managerId)}</td>
                    <td>{fmtDMY(p.closedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    if (filterType === "employees") {
      return (
        <div className="w-full overflow-x-auto">
          <table className="tbl tbl-report tbl-report--employees">
            <thead>
              <tr>
                <th>#</th>
                <th>Сотрудник</th>
                <th>Отдел (основной)</th>
                <th>План (ч)</th>
                <th>Факт (ч)</th>
                <th>Кол-во задач</th>
              </tr>
            </thead>
            <tbody>
              {results.map((e, idx) => {
                const deptName = getPrimaryDeptName(e, safeDb);
                const stats = employeeStatsById.get(e.id) || EMPTY_STATS;
                return (
                  <tr key={e.id}>
                    <td>{idx + 1}</td>
                    <td>
                      <b>
                        {e.last} {e.first}
                      </b>
                    </td>
                    <td>{deptName}</td>
                    <td>{stats.plan}</td>
                    <td>{stats.fact}</td>
                    <td>{stats.count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    if (filterType === "worklog") {
      return (
        <div className="w-full overflow-x-auto">
          <table className="tbl tbl-report tbl-report--worklog">
            <thead>
              <tr>
                <th>#</th>
                <th>Дата</th>
                <th>Сотрудник</th>
                <th>Задача</th>
                <th>Проект</th>
                <th>Часы</th>
                <th>Комментарий</th>
              </tr>
            </thead>
            <tbody>
              {results.map((l, idx) => {
                const project = projectsById.get(l.projectId);
                const user = employeesById.get(l.userId);
                return (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{fmtDMY(l.date)}</td>
                    <td>{user ? `${user.last} ${user.first}` : "-"}</td>
                    <td>{l.taskTitle}</td>
                    <td>{project?.code || "-"}</td>
                    <td>
                      <b>{l.hours}</b>
                    </td>
                    <td>{l.note || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    if (filterType === "wip") {
      const withRate = results.filter((r) => r.revenue != null);
      const withoutRate = results.filter((r) => r.revenue == null);
      const totalHours = withRate.reduce((s, r) => s + r.hours, 0);
      const totalRevenue = withRate.reduce((s, r) => s + r.revenue, 0);
      const zeroHours = withoutRate.reduce((s, r) => s + r.hours, 0);

      return (
        <div className="w-full overflow-x-auto">
          <table className="tbl tbl-report tbl-report--wip">
            <thead>
              <tr>
                <th>#</th>
                <th>Проект (код)</th>
                <th>Номер договора</th>
                <th>Заказчик</th>
                <th>Ставка ₽/ч</th>
                <th>Часы НЗП за период</th>
                <th>Затраты ₽</th>
              </tr>
            </thead>
            <tbody>
              {withRate.map((r, idx) => (
                <tr key={r.project.id}>
                  <td>{idx + 1}</td>
                  <td>
                    <b>{r.project.code}</b>
                  </td>
                  <td>{r.project.contractNumber || "-"}</td>
                  <td>{r.project.customer || "-"}</td>
                  <td>{formatRate(r.rate)}</td>
                  <td>{r.hours}</td>
                  <td>
                    <b>{formatRub(r.revenue)}</b>
                  </td>
                </tr>
              ))}
              <tr className="tbl-total-row">
                <td colSpan={5}>
                  <b>Итого</b>
                </td>
                <td>
                  <b>{totalHours}</b>
                </td>
                <td>
                  <b>{formatRub(totalRevenue)}</b>
                </td>
              </tr>
            </tbody>
          </table>

          {withoutRate.length > 0 && (
            <div className="mt-3">
              <b>
                Без ставки: {withoutRate.length} проектов, {zeroHours} часов
              </b>
              <table className="tbl tbl-report tbl-report--wip tbl-report--wip-without mt-2">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Проект (код)</th>
                    <th>Номер договора</th>
                    <th>Заказчик</th>
                    <th>Часы НЗП за период</th>
                    <th>Затраты ₽</th>
                  </tr>
                </thead>
                <tbody>
                  {withoutRate.map((r, idx) => (
                    <tr key={r.project.id}>
                      <td>{idx + 1}</td>
                      <td>
                        <b>{r.project.code}</b>
                      </td>
                      <td>{r.project.contractNumber || "-"}</td>
                      <td>{r.project.customer || "-"}</td>
                      <td>{r.hours}</td>
                      <td>-</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    }
  };

  const showDateRange = filterType !== "employees";
  const showDeadlineRange = filterType === "tasks" || filterType === "projects";
  const showClosedRange = filterType === "tasks" || filterType === "projects";
  const showStatusPriority = filterType === "tasks";
  const showCustomer = filterType !== "wip";

  return (
    <div className="rep">
      <div className="rep-panel p-4">
        <div className="rep-panel-title">Фильтры отчёта</div>

        <div className="toolbar mb-2">
          <label className="lbl m-0">Тип отчёта:</label>
          <div className="seg">
            {availableReportTypes.map((typeOption) => (
              <button
                key={typeOption.value}
                className={`seg-btn${filterType === typeOption.value ? " on" : ""}`}
                onClick={() => handleFilterChange("type", typeOption.value)}
              >
                {typeOption.label}
              </button>
            ))}
          </div>
        </div>

        <div className="toolbar filters-row">
          {showDateRange && (
            <div className="filter-group">
              <label className="lbl m-0">
                {filterType === "worklog"
                  ? "Дата записи:"
                  : filterType === "projects"
                    ? "Начало:"
                    : filterType === "wip"
                      ? "Период НЗП:"
                      : "ДАТА СОЗДАНИЯ:"}
              </label>
              <input
                className="inp w-150"
                type="date"
                value={dateFrom}
                onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              />
              <span className="filter-group-sep">-</span>
              <input
                className="inp w-150"
                type="date"
                value={dateTo}
                onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              />
            </div>
          )}

          {showDeadlineRange && (
            <div className="filter-group">
              <label className="lbl m-0">
                {filterType === "projects" ? "Окончание:" : "Срок исполнения:"}
              </label>
              <input
                className="inp w-150"
                type="date"
                value={deadlineFrom}
                onChange={(e) =>
                  handleFilterChange("deadlineFrom", e.target.value)
                }
              />
              <span className="filter-group-sep">-</span>
              <input
                className="inp w-150"
                type="date"
                value={deadlineTo}
                onChange={(e) =>
                  handleFilterChange("deadlineTo", e.target.value)
                }
              />
            </div>
          )}

          {showClosedRange && (
            <div className="filter-group">
              <label className="lbl m-0">Дата закрытия:</label>
              <input
                className="inp w-150"
                type="date"
                value={closedFrom}
                onChange={(e) =>
                  handleFilterChange("closedFrom", e.target.value)
                }
              />
              <span className="filter-group-sep">-</span>
              <input
                className="inp w-150"
                type="date"
                value={closedTo}
                onChange={(e) => handleFilterChange("closedTo", e.target.value)}
              />
            </div>
          )}

          <div className="filter-group">
            <label className="lbl m-0">Проект:</label>
            <Select
              className="w-180"
              value={projectId}
              onChange={(v) => handleFilterChange("projectId", v)}
              options={projectSelectOptions}
            />
          </div>

          {filterType !== "wip" && (
            <div className="filter-group">
              <label className="lbl m-0">Исполнитель:</label>
              <Select
                className="w-180"
                value={assigneeId}
                onChange={(v) => handleFilterChange("assigneeId", v)}
                options={assigneeSelectOptions}
              />
            </div>
          )}

          {showCustomer && (
            <div className="filter-group">
              <label className="lbl m-0">Заказчик:</label>
              <input
                className="inp w-200"
                type="text"
                value={customer}
                onChange={(e) => handleFilterChange("customer", e.target.value)}
                placeholder="поиск по названию"
              />
            </div>
          )}

          {showStatusPriority && (
            <>
              <div className="filter-group">
                <label className="lbl m-0">Статус задачи:</label>
                <Select
                  className="w-140"
                  value={status}
                  onChange={(v) => handleFilterChange("status", v)}
                  options={STATUS_SELECT_OPTIONS}
                />
              </div>
              <div className="filter-group">
                <label className="lbl m-0">Приоритет:</label>
                <Select
                  className="w-140"
                  value={priority}
                  onChange={(v) => handleFilterChange("priority", v)}
                  options={PRIORITY_SELECT_OPTIONS}
                />
              </div>
            </>
          )}
        </div>

        <div className="mt-3 flex gap-3 flex-wrap">
          <button className="btn ghost" onClick={resetFilters}>
            Сбросить фильтры
          </button>
          <div className="flex gap-2 items-center">
            <input
              className="inp w-180"
              type="text"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="Название шаблона"
            />
            <button className="btn ghost" onClick={saveFilter}>
              <Ic d={ICONS.plus} size={13} /> Сохранить фильтр
            </button>
          </div>
        </div>
      </div>

      {savedFilters.length > 0 && (
        <div className="rep-panel p-4">
          <div className="rep-panel-title">Сохранённые шаблоны</div>
          <div className="flex flex-wrap gap-3">
            {savedFilters.map((f) => {
              const criteria = [];
              const typeLabel =
                REPORT_TYPES.find((t) => t.value === f.filters.type)?.label ||
                "Задачи";
              criteria.push(`Тип: ${typeLabel}`);
              if (f.filters.dateFrom || f.filters.dateTo) {
                const from = f.filters.dateFrom
                  ? fmtDMY(f.filters.dateFrom)
                  : "";
                const to = f.filters.dateTo ? fmtDMY(f.filters.dateTo) : "";
                criteria.push(`Период: ${from} - ${to}`);
              }
              if (f.filters.deadlineFrom || f.filters.deadlineTo) {
                const from = f.filters.deadlineFrom
                  ? fmtDMY(f.filters.deadlineFrom)
                  : "";
                const to = f.filters.deadlineTo
                  ? fmtDMY(f.filters.deadlineTo)
                  : "";
                criteria.push(`Срок исполнения: ${from} - ${to}`);
              }
              if (f.filters.closedFrom || f.filters.closedTo) {
                const from = f.filters.closedFrom
                  ? fmtDMY(f.filters.closedFrom)
                  : "";
                const to = f.filters.closedTo ? fmtDMY(f.filters.closedTo) : "";
                criteria.push(`Закрыто: ${from} - ${to}`);
              }
              if (f.filters.projectId !== "all") {
                const proj = projectsById.get(f.filters.projectId);
                criteria.push(`Проект: ${proj?.code || "-"}`);
              }
              if (f.filters.assigneeId !== "all") {
                const emp = employeesById.get(f.filters.assigneeId);
                criteria.push(`Исполнитель: ${emp ? emp.last : "-"}`);
              }
              if (f.filters.status !== "all") {
                criteria.push(
                  `Статус: ${TASK_STATUSES[f.filters.status]?.label || f.filters.status}`,
                );
              }
              if (f.filters.priority !== "all") {
                criteria.push(
                  `Приоритет: ${PRIORITIES[f.filters.priority]?.label || f.filters.priority}`,
                );
              }
              if (f.filters.customer)
                criteria.push(`Заказчик: ${f.filters.customer}`);
              const displayText = criteria.length
                ? criteria.join(" · ")
                : "Все";

              return (
                <div
                  key={f.id}
                  className="pj-card cursor-pointer p-3 relative saved-filter-card"
                >
                  <div className="font-bold text-sm mb-1">{f.name}</div>
                  <div className="text-xs text-mut leading-tight">
                    {displayText}
                  </div>
                  <button
                    className="icon-btn absolute top-1 right-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSavedFilter(f.id);
                    }}
                    title="Удалить шаблон"
                  >
                    <Ic d={ICONS.x} size={14} />
                  </button>
                  <div className="mt-2">
                    <button
                      className="btn ghost sm"
                      onClick={() => loadFilter(f)}
                    >
                      Загрузить
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rep-panel p-4">
        <div className="flex justify-between items-center mb-3">
          <div className="rep-panel-title m-0">
            Результаты ({results.length})
          </div>
          <button
            className="btn primary sm"
            onClick={handleExport}
            disabled={results.length === 0}
          >
            <Ic d={ICONS.download} size={13} /> Выгрузить CSV
          </button>
        </div>
        {renderResults()}
      </div>
    </div>
  );
}

export default Reports;
